import { useState, useContext, useEffect, useRef } from "react";
import TevelCyberLogo from "../../../assets/img/TevelCyberFinalBlueLogo.png";
import LoginImage1 from "../../../assets/img/LoginIllustraion.png";
import { useNavigate } from "react-router-dom";
import LoginService from "../../../services/LoginService";
import AuthContext from "../../../auth/AuthContext";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import AlertService from "../../../services/AlertService";
import CyberBay from "../../../assets/img/CyberBayFinalBlueLogo.png";
import userIcon from "../../../assets/icons/UserLoginIcon.svg";
import lockIcon from "../../../assets/icons/LockIcon.svg";
import TermsModal from "./TermsModal";

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({ userName: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [loginData, setLoginData] = useState(null);
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const submitControllerRef = useRef(null);
  const termsControllerRef = useRef(null);

  useEffect(() => {
    if (authContext?.mfaVerified) {
      navigate("/");
    }
  }, [authContext?.mfaVerified, navigate]);

  const validateForm = () => {
    let newErrors = { userName: "", password: "" };
    const userNameRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!userName.trim()) {
      newErrors.userName = "Email is required";
    } else if (!userNameRegex.test(userName)) {
      newErrors.userName = "Enter a valid email address";
    }

    if (!password.trim()) {
      newErrors.password = "Password is required";
    } else if (password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    }

    setErrors(newErrors);
    return !newErrors.userName && !newErrors.password;
  };

  const getRedirectPath = (data) => {
    if (data.resetPassword) return "/reset";
    if (data.mfaSetupEnabled && !data.mfaSetupCompleted) return "/qrcode";
    if (!data.isAnswered) return "/questions";
    if (data.mfaSetupEnabled && data.mfaSetupCompleted) return "/mfa";
    return "/";
  };

const handleSubmit = async (event) => {
  event.preventDefault();
  if (!validateForm()) return;

  if (submitControllerRef.current) {
    submitControllerRef.current.abort();
  }
  const controller = new AbortController();
  submitControllerRef.current = controller;

  setLoading(true);

  const payload = {
    userName,
    password,
    clientId: process.env.REACT_APP_CLIENTID,
    clientSecret: process.env.REACT_APP_CLIENTSECRET,
  };

  try {
    const result = await LoginService.Login(
      payload,
      controller.signal
    );

    if (controller.signal.aborted) return;

    if (!result.isSuccess) {
      AlertService.error(result.errorMessage);
      return;
    }

    const data = result.data;
    setLoginData(data);
    authContext.login(
      data.access_token,
      data.refresh_token,
      data.identifier,
      data.id,
      data.roleId,
      data.isAnswered,
      data.resetPassword,
      data.mfaSetupCompleted,
      data.mfaSetupEnabled,
      data.mfaSetupEnabled ? false : true,
      data.hasOngoingValidations,
      data.displayName,
      data.menuPermissions,
      data.course_count,
      data.impersonate,
      data.impersonatedUserId
    );
    authContext.setPermissions(data.menuPermissions);

    if (!data.isTermsAccepted) {
      setShowTerms(true);
    } else {
      navigate(getRedirectPath(data));
    }
  } catch (error) {
    if (error.name === "AbortError") return;
    console.error(error);
    AlertService.error("Something went wrong");
  } finally {
    if (!controller.signal.aborted) setLoading(false);
  }
};

const handleAcceptTerms = async () => {
  setShowTerms(false);

  if (termsControllerRef.current) {
    termsControllerRef.current.abort();
  }
  const controller = new AbortController();
  termsControllerRef.current = controller;

  if (loginData) {
    try {
      await LoginService.AcceptTerms(
        loginData.id,
        controller.signal
      );

      if (controller.signal.aborted) return;

      navigate(getRedirectPath(loginData));
    } catch (err) {
      if (err.name === "AbortError") return;
      AlertService.error("Failed to accept terms");
    }
  }
};

useEffect(() => {
  return () => {
    submitControllerRef.current?.abort();
    termsControllerRef.current?.abort();
  };
}, []);

  const handleDeclineTerms = () => {
    setLoginData();
    AlertService.error("You must accept Terms & Conditions to continue.");
    setShowTerms(false);
  };

  return (
    <div className="container-fluid min-vh-100 d-flex flex-column flex-xl-row p-0">
      {/* Left Section */}
      {/* Desktop Logo */}
      <div className="col-xl-6 d-flex flex-column justify-content-center align-items-center p-3 p-lg-5 min-vh-100  ">
        <div className="d-none d-xl-block">
          <img
            src={TevelCyberLogo}
            alt="Company Logo"
            style={{ maxWidth: "217px", marginRight: "220px" }}
            className="img-fluid"
          />
        </div>
        {/* Full Height Flex to Center Logos + Form */}
        <div className="d-flex flex-column justify-content-center align-items-center flex-grow-1 w-100">
          <div className="d-flex d-xl-none justify-content-between align-items-center gap-5 mt-3 mb-4">
            <img
              src={TevelCyberLogo}
              alt="Tevel Cyber Logo"
              className="img-fluid"
              style={{ width: "140px" }}
            />
            <img
              src={CyberBay}
              alt="Cyber Bay Logo"
              className="img-fluid"
              style={{ width: "120px" }}
            />
          </div>
          {/* Form Block */}
          <div className="w-100 mb-xl-4" style={{ maxWidth: "440px" }}>
            <h4 className="mb-3  text-center fw-bold">
              Sign In to your Account
            </h4>
            <p className="text-secondary mb-4  text-center">
              Please enter your details
            </p>

            <form onSubmit={handleSubmit}>
              {/* Email */}
              <div className="mb-4 position-relative">
                <div className="input-group">
                  <span className="input-group-text bg-transparent border-end-0">
                    <img
                      src={userIcon}
                      alt="userIcon"
                      style={{ color: "#ccc" }}
                      className="fs-5"
                    />
                  </span>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    className="form-control border-start-0 custom-input"
                    placeholder="Email"
                    style={{ paddingLeft: "5px" }}
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                  />
                </div>
                {errors.userName && (
                  <p className="text-danger fw-normal smallFont">
                    {errors.userName}
                  </p>
                )}
              </div>

              {/* Password */}
              <div className="mb-1 position-relative">
                <div className="input-group">
                  <span className="input-group-text bg-transparent border-end-0">
                    <img src={lockIcon} alt="lockIcon" className="fs-5" />
                  </span>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="fake-password"
                    id="password"
                    className="form-control border-start-0 border-end-0 custom-input"
                    placeholder="Password"
                    style={{ paddingLeft: "5px" }}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    maxLength={30}
                  />
                  <span
                    className="input-group-text bg-transparent border-start-0"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{ cursor: "pointer", color: "#888" }}
                  >
                    {showPassword ? (
                      <FaEyeSlash className="fs-5" />
                    ) : (
                      <FaEye className="fs-5" />
                    )}
                  </span>
                </div>
                {errors.password && (
                  <p className="text-danger fw-normal error-bottom smallFont mt-1">
                    {errors.password}
                  </p>
                )}
              </div>

              <div className="d-flex justify-content-between mb-4">
                <div className="form-check"></div>
                <a
                  href="#/forgot"
                  className="text-decoration-none link mt-1 mt-lg-0"
                >
                  Forgot password?
                </a>
              </div>

              <button
                type="submit"
                className="btn btn-primary w-100 mb-3 custom-input"
                disabled={loading}
              >
                {loading ? (
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                ) : null}
                {loading ? "Signing in..." : "Sign In"}
              </button>
            </form>
          </div>
        </div>
        <footer className="mt-auto text-center w-100">
          <p className="text-muted small mb-0">
            © 2026 Tevel Cyber Corps Pvt. Ltd. All rights reserved - Version 2.2.0 -{" "}
            <a
              href="#/terms-and-conditions"
              className="link"
              target="_blank"
              style={{ textDecoration: "underline", cursor: "pointer" }}
            >
              Terms & Conditions
            </a>
          </p>
        </footer>

      </div>
      {/* Right Section */}
      <div className="col-xl-6 d-none d-xl-flex flex-column login-bg  min-vh-100 align-items-center justify-content-center  ">
        <div className="p-5 mt-2">
          <img
            src={CyberBay}
            alt="CYBER BAY"
            className="img-fluid mb-4"
            width="130px"
          />
        </div>
        <div className='"d-flex flex-column justify-content-center align-items-center flex-grow-1 w-100 p-xl-5'>
          <img
            src={LoginImage1}
            alt="Decorative"
            className="img-fluid loginImg"
            style={{
              maxWidth: "690px",
              maxHeight: "484px",
              marginLeft: "130px",
            }}
          />
        </div>
      </div>
      <TermsModal
        show={showTerms}
        onAccept={handleAcceptTerms}
        onDecline={handleDeclineTerms}
      />
    </div>
  );
};

export default Login;
